import { Box } from "@chakra-ui/react";
import CountTag from "./CountTag";

/**
 * SubTabs — segmented pill tab control (`.sub-tabs`).
 * design ref: styles.css:1326–1343, drawers.jsx:558–569 (sm),
 *             components.jsx:365 (md, LunaPopover).
 *
 * Inline-flex container with `neutral.100` background and 2px inner padding;
 * each cell is a rounded button. The active cell lifts on a white surface
 * (`neutral.25`) with a soft `0 1px 2px rgba(0,0,0,0.06)` shadow.
 *
 * `size="sm"` (drawer) shrinks horizontal padding; `size="md"` (LunaPopover)
 * matches the spec values directly.
 */

export interface SubTabItem<K extends string = string> {
  key: K;
  label: string;
  /** Number, or a string like "2/4" for fractional progress. */
  count?: number | string;
  hidden?: boolean;
}

export interface SubTabsProps<K extends string = string> {
  tabs: SubTabItem<K>[];
  activeKey: K;
  onChange: (key: K) => void;
  /** sm = compact (drawer), md = default (LunaPopover). */
  size?: "sm" | "md";
}

const SIZE: Record<
  NonNullable<SubTabsProps["size"]>,
  { px: string; py: string }
> = {
  // sm — drawer use; tightens horizontal padding to fit dense checklist headers.
  sm: { px: "sm", py: "2xs" }, // 8 / 4
  // md — styles.css:1334 (6×12)
  md: { px: "md", py: "1xs" }, // 12 / 6
};

const SubTabs = <K extends string = string>({
  tabs,
  activeKey,
  onChange,
  size = "md",
}: SubTabsProps<K>) => {
  const visible = tabs.filter((t) => !t.hidden);
  const dims = SIZE[size];
  return (
    <Box
      display="inline-flex"
      gap="2px" // tight rail between cells; matches design reference
      p="2px" // styles.css:1330
      bg="neutral.100"
      borderRadius="component.lg" // 8px (styles.css:1329)
    >
      {visible.map((tab) => {
        const isActive = tab.key === activeKey;
        return (
          <Box
            as="button"
            type="button"
            key={tab.key}
            onClick={() => onChange(tab.key)}
            display="inline-flex"
            alignItems="center"
            gap="1xs" // 6px (styles.css:1333)
            px={dims.px}
            py={dims.py}
            fontSize="smd" // 13px (styles.css:1335)
            fontWeight={500}
            lineHeight="1"
            color={isActive ? "neutral.950" : "neutral.700"}
            bg={isActive ? "neutral.25" : "transparent"}
            borderRadius="component.md" // 6px (styles.css:1337)
            // styles.css:1342 — soft pop-out shadow on the active cell.
            boxShadow={isActive ? "0 1px 2px rgba(0,0,0,0.06)" : "none"}
            cursor="pointer"
            transition="background-color 120ms, color 120ms, box-shadow 120ms"
            _hover={
              isActive
                ? undefined
                : {
                    color: "neutral.950",
                  }
            }
            _focusVisible={{
              outline: "none",
              boxShadow: isActive
                ? "0 1px 2px rgba(0,0,0,0.06), 0 0 0 3px rgba(113,122,247,0.25)"
                : "focusRing",
            }}
          >
            <Box as="span">{tab.label}</Box>
            {tab.count !== undefined && <CountTag count={tab.count} />}
          </Box>
        );
      })}
    </Box>
  );
};

export default SubTabs;
